<template>
  <div class="change-password-page">
    <header class="header">
      <el-button text class="back-btn" aria-label="返回" @click="goBack">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <h1 class="title">修改密码</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <el-card class="form-card" shadow="never">
        <div class="form-intro">
          <el-icon class="intro-icon" :size="36" color="var(--primary-color)">
            <Lock />
          </el-icon>
          <p class="form-tip">为了您的账户安全，请定期修改密码</p>
        </div>

        <el-form
          ref="formRef"
          :model="form"
          :rules="rules"
          label-position="top"
          class="password-form"
          @submit.prevent
        >
          <el-form-item label="原密码" prop="oldPassword">
            <el-input
              v-model="form.oldPassword"
              type="password"
              placeholder="请输入原密码"
              show-password
              clearable
              autocomplete="current-password"
              size="large"
            />
          </el-form-item>

          <el-form-item label="新密码" prop="newPassword">
            <el-input
              v-model="form.newPassword"
              type="password"
              placeholder="请输入新密码（6-20位）"
              show-password
              clearable
              autocomplete="new-password"
              size="large"
            />
          </el-form-item>

          <div v-if="form.newPassword" class="password-strength">
            <span class="strength-label">密码强度</span>
            <div class="strength-bars">
              <div
                v-for="n in 4"
                :key="n"
                class="strength-bar"
                :class="{ active: strengthLevel >= n, ['level-' + strengthLevel]: strengthLevel >= n }"
              />
            </div>
            <span class="strength-text" :class="'level-' + strengthLevel">
              {{ strengthText }}
            </span>
          </div>

          <el-form-item label="确认新密码" prop="confirmPassword">
            <el-input
              v-model="form.confirmPassword"
              type="password"
              placeholder="请再次输入新密码"
              show-password
              clearable
              autocomplete="new-password"
              size="large"
            />
          </el-form-item>

          <el-button
            type="primary"
            class="submit-btn"
            size="large"
            :loading="isSubmitting"
            @click="handleSubmit"
          >
            确认修改
          </el-button>
        </el-form>
      </el-card>
    </main>
  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Lock } from '@element-plus/icons-vue'
import request from '@/utils/request'
import md5 from 'js-md5'

export default {
  name: 'ChangePassword',
  components: { Lock },
  setup() {
    const router = useRouter()
    const formRef = ref(null)
    const isSubmitting = ref(false)

    const form = reactive({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    })

    const validateNewPassword = (_rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入新密码'))
        return
      }
      if (value.length < 6 || value.length > 20) {
        callback(new Error('密码长度应为6-20位'))
        return
      }
      if (value === form.oldPassword) {
        callback(new Error('新密码不能与原密码相同'))
        return
      }
      callback()
    }

    const validateConfirmPassword = (_rule, value, callback) => {
      if (!value) {
        callback(new Error('请确认新密码'))
        return
      }
      if (value !== form.newPassword) {
        callback(new Error('两次输入的密码不一致'))
        return
      }
      callback()
    }

    const rules = {
      oldPassword: [
        { required: true, message: '请输入原密码', trigger: 'blur' }
      ],
      newPassword: [
        { required: true, validator: validateNewPassword, trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, validator: validateConfirmPassword, trigger: 'blur' }
      ]
    }

    const strengthLevel = computed(() => {
      const pwd = form.newPassword
      if (!pwd) return 0
      let level = 0
      if (pwd.length >= 6) level++
      if (/[a-zA-Z]/.test(pwd)) level++
      if (/\d/.test(pwd)) level++
      if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) level++
      return level
    })

    const strengthText = computed(() => {
      const texts = ['', '弱', '中', '强', '非常强']
      return texts[strengthLevel.value] || ''
    })

    const handleSubmit = async () => {
      if (isSubmitting.value) return
      const formEl = formRef.value
      if (!formEl) return

      try {
        await formEl.validate()
      } catch {
        return
      }

      isSubmitting.value = true
      try {
        const response = await request.post('/uic-user/update-password', {
          oldPassword: md5(form.oldPassword),
          newPassword: md5(form.newPassword),
          newPassword1: md5(form.confirmPassword)
        })

        if (response.success) {
          ElMessage.success('密码修改成功')
          router.back()
        } else {
          ElMessage.error(response.message || response.msg || '密码修改失败')
        }
      } catch (error) {
        console.error('修改密码失败:', error)
        ElMessage.error(error?.message || '网络错误，请稍后重试')
      } finally {
        isSubmitting.value = false
      }
    }

    const goBack = () => router.back()

    return {
      formRef,
      form,
      rules,
      isSubmitting,
      strengthLevel,
      strengthText,
      handleSubmit,
      goBack
    }
  }
}
</script>

<style scoped>
.change-password-page {
  min-height: 100vh;
  background: var(--background-color);
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  color: var(--header-text);
}

.back-icon {
  width: 22px;
  height: 22px;
}

.title {
  margin: 0;
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--header-text);
}

.header-right {
  width: 36px;
}

.content {
  padding: 12px;
  margin-top: calc(52px + env(safe-area-inset-top));
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
}

.form-card {
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
}

.form-card :deep(.el-card__body) {
  padding: 16px;
}

.form-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-color);
}

.form-tip {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  text-align: center;
}

.password-form :deep(.el-form-item__label) {
  font-weight: 500;
  color: var(--text-primary);
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -4px 0 14px;
}

.strength-label {
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--text-secondary);
}

.strength-bars {
  display: flex;
  flex: 1;
  gap: 4px;
  height: 6px;
}

.strength-bar {
  flex: 1;
  border-radius: 3px;
  background: var(--border-color);
  transition: background-color 0.2s ease;
}

.strength-bar.active.level-1 {
  background: #ef4444;
}

.strength-bar.active.level-2 {
  background: #f59e0b;
}

.strength-bar.active.level-3 {
  background: #10b981;
}

.strength-bar.active.level-4 {
  background: #3b82f6;
}

.strength-text {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
}

.strength-text.level-1 {
  color: #ef4444;
}

.strength-text.level-2 {
  color: #f59e0b;
}

.strength-text.level-3 {
  color: #10b981;
}

.strength-text.level-4 {
  color: #3b82f6;
}

.submit-btn {
  width: 100%;
  height: 44px;
  margin-top: 8px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
}
</style>
